import { doc, updateDoc, setDoc, serverTimestamp, deleteDoc, getDoc, increment, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase';

export const startBonusTracking = async (userId, bonusId) => {
  try {
    console.log('Starting bonus tracking:', { userId, bonusId });
    
    const userBonusRef = doc(db, 'userBonuses', `${userId}_${bonusId}`);
    const bankBonusRef = doc(db, 'users', userId, 'bankBonuses', bonusId);
    const bankRef = doc(db, 'banks', bonusId);

    // First check if documents exist
    const [userBonusDoc, bankBonusDoc, bankDoc] = await Promise.all([
      getDoc(userBonusRef),
      getDoc(bankBonusRef),
      getDoc(bankRef)
    ]);

    console.log('Current document states:', {
      userBonusExists: userBonusDoc.exists(),
      bankBonusExists: bankBonusDoc.exists(),
      userBonusData: userBonusDoc.exists() ? userBonusDoc.data() : null,
      bankBonusData: bankBonusDoc.exists() ? bankBonusDoc.data() : null
    });

    // If either document exists with an active status, prevent starting
    if (userBonusDoc.exists() && userBonusDoc.data().status === 'in_progress') {
      throw new Error('Bonus already exists for this bank');
    }

    const now = new Date();
    const trackingData = {
      status: 'in_progress',
      startedAt: now,
      completedAt: null,
      lastUpdated: now,
      trackingStarted: true,
      userId,
      bankId: bonusId,
      bankName: bankDoc.exists() ? bankDoc.data().name : '',
      amount: bankDoc.exists() ? bankDoc.data().amount : 0,
      progress: 0,
      progressPercentage: 0,
      isPaidOut: false,
      userProgress: {
        status: 'in_progress',
        startedAt: now,
        completedAt: null,
        progress: 0,
        progressPercentage: 0,
        lastUpdated: now
      }
    };

    // Create both documents with setDoc
    const batch = writeBatch(db);
    batch.set(userBonusRef, trackingData);
    batch.set(bankBonusRef, trackingData);
    await batch.commit();

    console.log('Successfully started tracking');
    return trackingData;
  } catch (error) {
    console.error('Error starting bonus tracking:', error);
    throw error;
  }
};

export const completeBankBonus = async (userId, bonusId, isPaidOut = false, bonusAmount = 0) => {
  try {
    console.log('Starting complete bonus process:', { userId, bonusId, isPaidOut, bonusAmount });
    
    const userBonusRef = doc(db, 'userBonuses', `${userId}_${bonusId}`);
    const bankBonusRef = doc(db, 'users', userId, 'bankBonuses', bonusId);
    const userStatsRef = doc(db, 'users', userId, 'stats', 'bonuses');
    const bankRef = doc(db, 'banks', bonusId);
    
    // Get the current bonus data to preserve startedAt
    const [userBonusDoc, bankBonusDoc, bankDoc] = await Promise.all([
      getDoc(userBonusRef),
      getDoc(bankBonusRef),
      getDoc(bankRef)
    ]);

    const userBonusData = userBonusDoc.exists() ? userBonusDoc.data() : {};
    const bankBonusData = bankBonusDoc.exists() ? bankBonusDoc.data() : {};
    const bankData = bankDoc.exists() ? bankDoc.data() : {};
    
    const now = new Date();
    const updateData = {
      completedAt: now,
      status: 'completed',
      isPaidOut: isPaidOut,
      bonusAmount: isPaidOut ? bonusAmount : 0,
      lastUpdated: now,
      userId,
      bonusId,
      progress: 100,
      progressPercentage: 100,
      // Preserve the original startedAt timestamp
      startedAt: userBonusData.startedAt || bankBonusData.startedAt || now,
      userProgress: {
        status: 'completed',
        progress: 100,
        progressPercentage: 100,
        startedAt: userBonusData.startedAt || bankBonusData.startedAt || now,
        completedAt: now,
        lastUpdated: now
      }
    };

    // Get current user stats
    const statsDoc = await getDoc(userStatsRef);
    const currentStats = statsDoc.exists() ? statsDoc.data() : {
      totalAttempted: 0,
      totalCompleted: 0,
      totalEarned: 0,
      successfulCompletions: 0,
      failedCompletions: 0
    };

    // Calculate new stats - use existing bank data
    const currentSuccessful = bankData.successfulCompletions || 0;
    const currentFailed = bankData.failedCompletions || 0;
    const currentTotal = bankData.totalAttempts || 0;

    const newSuccessfulCompletions = currentSuccessful + (isPaidOut ? 1 : 0);
    const newFailedCompletions = currentFailed + (isPaidOut ? 0 : 1);
    const newTotalAttempts = currentTotal + 1;
    
    // Calculate success rate as a percentage
    const successRate = newTotalAttempts > 0 
      ? Math.round((newSuccessfulCompletions / newTotalAttempts) * 100)
      : 0;

    // Calculate new user stats
    const newUserStats = {
      totalAttempted: (currentStats.totalAttempted || 0) + 1,
      totalCompleted: (currentStats.totalCompleted || 0) + 1,
      totalEarned: (currentStats.totalEarned || 0) + (isPaidOut ? bonusAmount : 0),
      successfulCompletions: (currentStats.successfulCompletions || 0) + (isPaidOut ? 1 : 0),
      failedCompletions: (currentStats.failedCompletions || 0) + (isPaidOut ? 0 : 1),
      lastUpdated: now
    };

    // Update bank document with new success rate
    const bankUpdateData = {
      totalAttempts: newTotalAttempts,
      successfulCompletions: newSuccessfulCompletions,
      failedCompletions: newFailedCompletions,
      successRate: successRate,
      lastUpdated: now
    };

    // Create/Update all documents using a batch
    const batch = writeBatch(db);
    batch.set(userBonusRef, updateData, { merge: true });
    batch.set(bankBonusRef, updateData, { merge: true });
    batch.set(userStatsRef, newUserStats, { merge: true });
    batch.update(bankRef, bankUpdateData);
    
    await batch.commit();

    console.log('Successfully completed bonus and updated stats:', {
      bonusUpdate: updateData,
      userStatsUpdate: newUserStats,
      bankUpdate: bankUpdateData,
      successRate: successRate
    });

    return {
      ...updateData,
      id: bonusId,
      userId,
      bonusAmount: isPaidOut ? bonusAmount : 0,
      successRate: successRate
    };
  } catch (error) {
    console.error('Error completing bonus:', {
      error,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    throw error;
  }
};

export const resetBonusTracking = async (userId, bonusId) => {
  try {
    console.log('Starting reset process:', { userId, bonusId });
    
    const userBonusRef = doc(db, 'userBonuses', `${userId}_${bonusId}`);
    const bankBonusRef = doc(db, 'users', userId, 'bankBonuses', bonusId);

    // Delete the userBonus document completely
    try {
      await deleteDoc(userBonusRef);
      console.log('Successfully deleted userBonus document');
    } catch (error) {
      console.log('Error deleting userBonus (might not exist):', error);
    }

    // Reset the bankBonus document to initial state
    const resetData = {
      status: 'not_started',
      startedAt: null,
      completedAt: null,
      isPaidOut: false,
      lastUpdated: serverTimestamp(),
      trackingStarted: false,
      // Make sure all tracking-related fields are explicitly reset
      bonusAmount: null,
      requirements: [],
      notes: '',
      progress: null,
      isTracking: false,
      isCompleted: false
    };

    // Use setDoc with merge: false to ensure a complete reset
    await setDoc(bankBonusRef, resetData, { merge: false });
    console.log('Successfully reset bankBonus document');

    // Verify the reset was successful
    const verifyDoc = await getDoc(bankBonusRef);
    const verifyData = verifyDoc.data();
    
    if (verifyData?.status !== 'not_started') {
      throw new Error('Failed to reset bonus status');
    }

    return true;
  } catch (error) {
    console.error('Reset failed:', error);
    throw error;
  }
};

export const formatFirestoreTimestamp = (timestamp) => {
  if (!timestamp) return 'Date not available';
  
  // Handle both Firestore Timestamp and seconds number
  const date = timestamp.seconds ? 
    new Date(timestamp.seconds * 1000) : 
    new Date(timestamp);
    
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export { db }; 